import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import {
  IEmergencia,
  IFilter,
  IQueryParam,
  IUbicacion,
  IUbicacionBase,
} from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { LoginService } from '../../../../login/login.service';
import {
  MapCardComponent,
  MapCardConfig,
} from '../../../../../componentes/map-card/map-card.component';
import {
  MapaFacilData,
  MarkerMapa,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { HospitalesLayerManager } from '../../../../../auxiliares/mapbox/entity-managers/hospitales-layer.manager';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { LoadingComponent } from '../../../../../componentes/loading/loading.component';
import { ButtonComponent } from '../../../../../auxiliares/button/button.component';
import { distanciaFormateada } from '../acciones-emergencia/movil-emergencia';

interface IHospitalConDistancia extends IUbicacionBase<'Hospital'> {
  distancia?: number;
}

/**
 * Elegir el hospital al que va la ambulancia (Rumbo a hospital). Ordena los
 * hospitales por distancia al lugar de la emergencia y devuelve el _id elegido.
 */
@Component({
  selector: 'app-selector-hospital-emergencia',
  templateUrl: './selector-hospital-emergencia.component.html',
  styleUrls: [
    '../selector-movil-emergencia/selector-movil-emergencia.component.scss',
    '../acciones-emergencia/acciones-emergencia-dialog.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatCardModule,
    MatDividerModule,
    MapCardComponent,
    CardHeaderComponent,
    LoadingComponent,
    ButtonComponent,
  ],
})
export class SelectorHospitalEmergenciaComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<SelectorHospitalEmergenciaComponent>);
  public data = inject<{ emergencia: IEmergencia }>(MAT_DIALOG_DATA);
  private lista2 = inject(Lista2Service);
  private helper = inject(HelperService);
  private hospitalesLayerManager = inject(HospitalesLayerManager);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(MapCardComponent) mapCardComponent?: MapCardComponent;

  public cargando = true;
  public hospitales: IHospitalConDistancia[] = [];
  public hospitalSeleccionado?: IHospitalConDistancia;
  public mapCardConfig?: MapCardConfig;
  public distanciaFormateada = distanciaFormateada;

  public get idHospitalActual(): string | undefined {
    return this.data?.emergencia?.ultimoEventoEmergencia?.detallesEmergencias
      ?.idHospital;
  }

  private async listarHospitales(): Promise<void> {
    const filter: IFilter<IUbicacion> = {
      idCliente: LoginService.getCliente()?._id,
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'identificacion',
      select: 'identificacion valores.gestion geojson',
      unlimited: true,
      includeChildren: true,
    };
    const res = await firstValueFrom(this.lista2.hospitals(query));
    const hospitales = (res?.datos ?? []) as IHospitalConDistancia[];

    const destino = this.data?.emergencia?.ubicacionDestino?.coordinates as
      | [number, number]
      | undefined;
    if (destino) {
      for (const h of hospitales) {
        const coord = h.geojson?.coordinates as [number, number] | undefined;
        if (coord) {
          h.distancia = this.helper.distanciaEntrePuntos(destino, coord);
        }
      }
      hospitales.sort(
        (a, b) => (a.distancia ?? Infinity) - (b.distancia ?? Infinity),
      );
    }
    this.hospitales = hospitales;
  }

  private generarMapa(): void {
    const markers: MarkerMapa[] = [];
    for (const h of this.hospitales) {
      const coord = h.geojson?.coordinates as [number, number] | undefined;
      if (!coord) continue;
      markers.push({
        label: 'hospital',
        coor: coord,
        text:
          this.hospitalSeleccionado?._id === h._id
            ? h.identificacion
            : undefined,
        id: h._id,
        data: { ...h, geojson: { type: 'Point', coordinates: coord } },
      });
    }

    const destino = this.data?.emergencia?.ubicacionDestino?.coordinates as
      | [number, number]
      | undefined;
    if (destino) {
      markers.push({
        label: 'emergenciaMedica',
        coor: destino,
        text: 'Emergencia',
        centrar: true,
        data: { ...this.data.emergencia },
      });
    }

    const mapData: MapaFacilData = {
      markers,
      center: destino ?? markers[0]?.coor ?? [0, 0],
      zoom: destino ? 14 : 13,
      selectorBaseLayer: true,
      clustering: { enabled: false },
    };

    this.mapCardConfig = {
      title: 'Mapa',
      icon: 'map',
      color: 'blue',
      mapOptions: mapData,
      nombreMap: 'selectorHospitales',
      disablePopups: true,
    };
  }

  public onMarkerClick(entity: any): void {
    const id = entity?.id ?? entity?.properties?.id;
    const hospital = this.hospitales.find((h) => h._id === id);
    if (hospital) this.seleccionar(hospital);
  }

  public seleccionar(hospital: IHospitalConDistancia): void {
    this.hospitalSeleccionado = hospital;
    this.hospitalesLayerManager.mostrarNombreDeHospital(hospital._id!);
    const coord = hospital.geojson?.coordinates as [number, number] | undefined;
    const map = this.mapCardComponent?.getMap();
    if (coord && map) {
      map.flyTo({ center: coord, zoom: 16, duration: 1500 });
    }
    this.cdr.markForCheck();
  }

  public confirmar(): void {
    if (this.hospitalSeleccionado) {
      this.dialogRef.close(this.hospitalSeleccionado._id);
    }
  }

  public cerrar(): void {
    this.dialogRef.close();
  }

  async ngOnInit(): Promise<void> {
    try {
      await this.listarHospitales();
      if (this.idHospitalActual) {
        this.hospitalSeleccionado = this.hospitales.find(
          (h) => h._id === this.idHospitalActual,
        );
      }
      this.generarMapa();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.cargando = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.hospitalesLayerManager.mostrarNombreDeHospital(null);
  }
}
